import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { ChatItems } from './ChatItems';
import { CHATS_KEY, MODEL_LABELS } from './WorkflowPage';

const viewUrl = (machineId: string, name: string) => `/api/v1/machines/${machineId}/inputs/view?name=${encodeURIComponent(name)}`;

/** Text and pictures for Claude; pictures go to the machine first (doc 33). */
function Composer({ chatId, machineId, busy }: { chatId: string; machineId: string; busy: boolean }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const upload = useMutation({ mutationFn: (file: File) => api.uploadInput(machineId, file), onSuccess: ({ image }) => { setImages((list) => [...list, image.name]); } });
  const send = useMutation({
    mutationFn: () => api.sendChatMessage(chatId, { text, images }),
    onSuccess: async () => { setText(''); setImages([]); await queryClient.invalidateQueries({ queryKey: ['chat', chatId] }); },
  });
  return (
    <form className="panel chat-composer" onSubmit={(e) => { e.preventDefault(); send.mutate(); }}>
      {images.length > 0 && (
        <ul className="chat-attachments" aria-label="Pictures to send">
          {images.map((name) => (
            <li key={name}><img src={viewUrl(machineId, name)} alt={name} />
              <button type="button" className="button-quiet" aria-label={`Remove ${name}`} onClick={() => { setImages((list) => list.filter((n) => n !== name)); }}>×</button></li>
          ))}
        </ul>
      )}
      <label htmlFor="chat-text" className="visually-hidden">Message to Claude</label>
      <textarea id="chat-text" rows={3} value={text} placeholder="e.g. Change her pose to sitting on a chair, keep the face and clothes" onChange={(e) => { setText(e.target.value); }} />
      {[upload.error, send.error].filter(Boolean).map((e, n) => <p key={n} role="alert" className="form-error">{errorMessage(e)}</p>)}
      <div className="chat-actions">
        <label className="button-link">
          {upload.isPending ? 'Uploading…' : 'Add a picture'}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="visually-hidden" disabled={upload.isPending || images.length >= 8}
            onChange={(e) => { const file = e.target.files?.[0]; if (file) upload.mutate(file); e.target.value = ''; }} />
        </label>
        <button type="submit" disabled={busy || send.isPending || upload.isPending || text.trim() === ''}>Send</button>
      </div>
    </form>
  );
}

/** One chat: what Claude did so far, live while it works (polled, so a phone can sleep). */
export function ChatPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ['chat', id], queryFn: () => api.getChat(id), refetchInterval: (q) => (q.state.data?.chat.status === 'running' ? 1500 : false) });
  const chat = detail.data?.chat;
  const items = detail.data?.items ?? [];
  const end = useRef<HTMLDivElement>(null);
  // New steps scroll into view, as in any chat.
  useEffect(() => { end.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }); }, [items.length]);
  const installs = useQuery({ queryKey: ['node-installs', chat?.machineId], queryFn: () => api.nodeInstalls(chat?.machineId ?? ''), enabled: Boolean(chat?.machineId), refetchInterval: 5000 });
  const stop = useMutation({ mutationFn: () => api.stopChat(id), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat', id] }) });
  const waiting = installs.data?.installs.filter((i) => i.status === 'awaiting_approval') ?? [];
  if (detail.isError) return <p role="alert" className="form-error">{errorMessage(detail.error)}</p>;
  if (!chat) return <p role="status">Loading…</p>;
  const running = chat.status === 'running';
  return (
    <section aria-labelledby="chat-title" className="chat-page">
      <p><Link to="/claude" onClick={() => { void queryClient.invalidateQueries({ queryKey: CHATS_KEY }); }}>← Claude</Link></p>
      <h1 id="chat-title">{chat.title}</h1>
      <p className="muted">{MODEL_LABELS[chat.model]}{chat.recipeId && <> · <Link to={`/workflows/${chat.recipeId}`}>Open the workflow</Link></>}</p>
      {items.length === 0 && <p className="muted">Add pictures if you have them, then say what you want.</p>}
      <ChatItems items={items} />
      <div ref={end} />
      {waiting.length > 0 && chat.machineId && (
        <p className="notice">Claude asked to install {waiting.map((w) => w.packId).join(', ')} — <Link to={`/machines/${chat.machineId}#nodepacks-title`}>approve or decline on the machine page</Link>.</p>
      )}
      {running && <p role="status" className="chat-working">Claude is working… <button type="button" className="button-quiet" disabled={stop.isPending} onClick={() => { stop.mutate(); }}>Stop</button></p>}
      {chat.status === 'failed' && <p role="alert" className="form-error">{chat.error}</p>}
      {chat.machineId ? <Composer chatId={chat.id} machineId={chat.machineId} busy={running} /> : <p className="notice">This chat's machine was removed.</p>}
    </section>
  );
}
